import { Skeleton } from '@mantine/core';
import useTranslation from 'next-translate/useTranslation';
import Link from 'next/link';
import { match } from 'ts-pattern';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';

import { Routes } from '~/domains/common/constants/routes';
import { Text } from '~/domains/design-system/Text';
import { IconChevronLeft } from '~/domains/design-system/icons/IconChevronLeft';
import { IconChevronRight } from '~/domains/design-system/icons/IconChevronRight';
import { GameSectionsContent } from '~/domains/common/components/GameSectionsList/GameSectionsList';
import { SectionStateCard } from '~/domains/common/components/SectionStateCard/SectionStateCard';
import repeat from '~/common/utils/repeat';

import type { PlayerCellProps } from '../PlayerCell/PlayerCell';

import { PopularPickCard } from './PopularPickCard';
import { useStyles } from './PopularPicksSection.styles';

const POPULAR_PICKS_COUNT = 8;

const PicksCarousel = memo(
  ({
    picks,
    isLoading,
    hideTeamLogo,
  }: {
    picks: PlayerCellProps[];
    isLoading: boolean;
    hideTeamLogo?: boolean;
  }) => {
    const { classes, theme } = useStyles();
    const scrollRef = useRef<HTMLDivElement>(null);
    const [isHovered, setIsHovered] = useState(false);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);

    const updateScrollState = useCallback(() => {
      const el = scrollRef.current;
      if (!el) return;
      setCanScrollLeft(el.scrollLeft > 0);
      setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 1);
    }, []);

    useEffect(() => {
      updateScrollState();
    }, [picks.length, updateScrollState]);

    const handleScrollLeft = useCallback(() => {
      scrollRef.current?.scrollBy({ left: -(180 + 8), behavior: 'smooth' });
    }, []);

    const handleScrollRight = useCallback(() => {
      scrollRef.current?.scrollBy({ left: 180 + 8, behavior: 'smooth' });
    }, []);

    return (
      <div
        className={classes.carouselWrapper}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {canScrollLeft && (
          <div className={classes.fadeLeft}>
            {isHovered && (
              <button
                type="button"
                className={classes.arrowButton}
                onClick={handleScrollLeft}
                aria-label="Scroll left"
              >
                <IconChevronLeft size={18} color={theme.colors.gray2[9]} />
              </button>
            )}
          </div>
        )}
        {canScrollRight && (
          <div className={classes.fadeRight}>
            {isHovered && (
              <button
                type="button"
                className={classes.arrowButton}
                onClick={handleScrollRight}
                aria-label="Scroll right"
              >
                <IconChevronRight size={18} color={theme.colors.gray2[9]} />
              </button>
            )}
          </div>
        )}

        <div ref={scrollRef} className={classes.cardsRow} onScroll={updateScrollState}>
          {isLoading
            ? repeat(4, (i) => (
                <div key={i} className={classes.skeletonCard}>
                  <Skeleton width={180} height={226} radius={theme.radius.lg} />
                </div>
              ))
            : picks.map((pick) => (
                <PopularPickCard
                  key={pick.id}
                  pick={pick}
                  className={classes.carouselCard}
                  hideTeamLogo={hideTeamLogo}
                />
              ))}
        </div>
      </div>
    );
  }
);

PicksCarousel.displayName = 'PicksCarousel';

function ViewAllLink({ href, onClick }: { href: string; onClick?: () => void }) {
  const { classes, theme } = useStyles();
  const { t } = useTranslation('contest');

  return (
    <Link href={href} className={classes.viewAllLink} onClick={onClick}>
      <Text variant="subhead-medium" color={theme.colors.gray2[4]}>
        {t('quickPick.board.popularPicks.viewAll')}
      </Text>
      <IconChevronRight size={16} color={theme.colors.gray2[4]} />
    </Link>
  );
}

function PopularPicksSection({
  items,
  isLoading,
  isError = false,
  viewAllHref = Routes.quickPicksPopular({}),
  onViewAll,
  fullWidth = false,
  hideTeamLogo = false,
}: {
  items: PlayerCellProps[];
  isLoading: boolean;
  isError?: boolean;
  viewAllHref?: string;
  onViewAll?: () => void;
  /** Stretch the section to the full container width (e.g. on the home page). */
  fullWidth?: boolean;
  hideTeamLogo?: boolean;
}) {
  const { t } = useTranslation('contest');
  const picks = items.slice(0, POPULAR_PICKS_COUNT);

  return (
    <GameSectionsContent
      fullWidth={fullWidth}
      sections={[
        {
          key: 'popular-picks',
          title: t('quickPick.board.popularPicks.title'),
          headerActions: <ViewAllLink href={viewAllHref} onClick={onViewAll} />,
          items: picks,
        },
      ]}
      renderSectionItems={() =>
        match({ isError, isLoading, isEmpty: picks.length === 0 })
          .returnType<ReactNode>()
          .with({ isError: true }, () => (
            <SectionStateCard
              title={t('quickPick.board.popularPicks.error.title')}
              subtitle={t('quickPick.board.popularPicks.error.subtitle')}
            />
          ))
          .with({ isLoading: false, isEmpty: true }, () => (
            <SectionStateCard
              title={t('quickPick.board.popularPicks.empty.title')}
              subtitle={t('quickPick.board.popularPicks.empty.subtitle')}
            />
          ))
          .otherwise(() => (
            <PicksCarousel picks={picks} isLoading={isLoading} hideTeamLogo={hideTeamLogo} />
          ))
      }
    />
  );
}

export default PopularPicksSection;
